import { DeleteOutlineOutlined, SaveOutlined } from "@mui/icons-material";
import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { ModalContext } from "../../../../Components/Modals";
import React from "react";
import ModalLoadWorkflowDiagramDesigner, {
  ModalLoadWorkflowDiagramDesignerElementProps,
} from "../../../../Components/Modals/ModalLoadWorkflowDiagramDesigner/modal-load-workflow-diagram-designer";
import ModalSaveDiagramDesigner, {
  ModalSaveDiagramDesignerElementProps,
} from "../../../../Components/Modals/ModalSaveDiagramDesigner/modal-save-diagram-designer";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import { useDispatch } from "react-redux";
import {
  AddWorkflow,
  DeleteWorkflowById,
  GetDiagramEdges,
  GetDiagramNodes,
  GetIsNewWorkflow,
  GetOrientation,
  GetWorkflow,
  GetWorkflows,
  InitNewWorkflow,
  SetIsNewWorkflow,
  SetOrientation,
  SetWorkflow,
  TriggerAutoLayout,
} from "../../../../Redux/Reducers/redux-slice-workflow-designer";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import SaveAsOutlinedIcon from "@mui/icons-material/SaveAsOutlined";
import { useSelector } from "react-redux";
import { DbKalikoWorkflowModelInfos, IKalikoNode, KalikoWorkflowLink } from "../../../../Core/Api/Types/kaliko";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { GenericResponseStd } from "../../../../Core/Api/Types/generic-response-std";
import { workflowService } from "../../../../Core/Api/services";
import AutoModeOutlinedIcon from "@mui/icons-material/AutoModeOutlined";
import VerticalSplitOutlinedIcon from "@mui/icons-material/VerticalSplitOutlined";
import HorizontalSplitOutlinedIcon from "@mui/icons-material/HorizontalSplitOutlined";
import { useTranslation } from "react-i18next";
interface KalikoHeaderProps {}

const KalikoHeader: React.FC<KalikoHeaderProps> = (props: KalikoHeaderProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const modalContext = React.useContext(ModalContext);
  const isNewWorkflow = useSelector(GetIsNewWorkflow);
  const diagramNodes = useSelector(GetDiagramNodes);
  const diagramEdges = useSelector(GetDiagramEdges);
  const workflow = useSelector(GetWorkflow);
  const workflows = useSelector(GetWorkflows);
  const orientation = useSelector(GetOrientation);

  const customSnackbar = useCustomSnackbar();
  const { openModal } = { ...modalContext };

  const handleAutoLayoutClick = () => {
    dispatch(TriggerAutoLayout());
  };

  const handleCreateOrUpdateWorkflowResponse = (
    response: GenericResponseStd,
    isNewWorkflow: boolean,
    workflowToSave: any
  ) => {
    if (response.success) {
      customSnackbar(t("alert:success.workflowSuccess", { context: isNewWorkflow ? "created" : "updated" }), "success");
      dispatch(SetIsNewWorkflow(false));
      dispatch(SetWorkflow(workflowToSave));
      if (isNewWorkflow) {
        dispatch(AddWorkflow(workflowToSave));
      }
    } else customSnackbar(t("alert:error.workflowError", { context: isNewWorkflow ? "created" : "updated" }), "error");
  };

  const saveWorkflow = (saveAsNew: boolean) => {
    const workflowLinks: KalikoWorkflowLink[] = [];
    for (let index = 0; index < diagramEdges.length; index++) {
      const element = diagramEdges[index];
      const linkSourceNode = diagramNodes.find((n) => n.id === element.source);
      const linkTargetNode = diagramNodes.find((n) => n.id === element.target);

      if (!linkSourceNode || !linkTargetNode) continue;

      const link: KalikoWorkflowLink = {
        name: element.data?.name,
        condition: element.data?.condition ?? null,
        sourceUid: linkSourceNode.data.uid,
        targetUid: linkTargetNode.data.uid,
      };
      workflowLinks.push(link);
    }

    const workflowNodes: IKalikoNode[] = [];
    for (let index = 0; index < diagramNodes.length; index++) {
      const element = diagramNodes[index];
      const node: IKalikoNode = {
        $type: element.data.$type,
        name: element.data.name,
        position: { x: Math.round(element.position.x), y: Math.round(element.position.y) },
        type: element.data.type,
        uid: element.data.uid,
        mode: element.data.mode,
        status: element.data.status,
        progress: element.data.progress,
        inputParams: element.data.inputParams,
        condition: element.data.condition,
        logs: element.data.logs,
      };
      workflowNodes.push(node);
    }
    const workflowToSave: DbKalikoWorkflowModelInfos = {
      ...workflow,
      id: isNewWorkflow || saveAsNew ? workflows.reduce((acc, w) => (w.id > acc ? w.id : acc), 0) + 1 : workflow.id,
      created: isNewWorkflow || saveAsNew ? null : workflow.created,
      updated: isNewWorkflow || saveAsNew ? null : workflow.updated,
      model: { ...workflow.model, links: workflowLinks, nodes: workflowNodes },
    };
    if (isNewWorkflow || saveAsNew) {
      workflowService
        .CreateWorkflowModels(workflowToSave)
        .then((response) => handleCreateOrUpdateWorkflowResponse(response, true, workflowToSave));
    } else {
      workflowService
        .UpdateWorkflowModels(workflowToSave)
        .then((response) => handleCreateOrUpdateWorkflowResponse(response, false, workflowToSave));
    }
  };
  const handleCreateNewWorkflowClick = () => {
    if (!openModal) return;

    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      title: t("alert:warning.createNewWorkflow"),
      onConfirm: () => {
        dispatch(InitNewWorkflow());
      },
      displayComment: false,
    });
  };

  const handleLoadWorkflowClick = () => {
    if (!openModal) return;

    openModal<ModalLoadWorkflowDiagramDesignerElementProps>(ModalLoadWorkflowDiagramDesigner, {
      onConfirm: () => {},
    });
  };

  const handleSaveAsWorkflowClick = () => {
    if (!openModal) return;

    openModal<ModalSaveDiagramDesignerElementProps>(ModalSaveDiagramDesigner, {
      onConfirm: () => {
        saveWorkflow(true);
      },
    });
  };

  const handleSaveWorkflowClick = () => {
    saveWorkflow(false);
  };

  const handleChangeOrientationClick = () => {
    dispatch(SetOrientation(orientation === "horizontal" ? "vertical" : "horizontal"));
  };

  const handleDeleteClick = () => {
    openModal &&
      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        onConfirm: () => {
          workflowService.DeleteKalikoBPMWorkflowModel(workflow.id).then((response) => {
            if (response.success) {
              customSnackbar(t("alert:success.workflowSuccess", { context: "deleted" }), "success");
              dispatch(DeleteWorkflowById(workflow.id));
            } else {
              customSnackbar(t("alert:error.workflowError", { context: "deleted" }), "error");
            }
          });
        },
        title: t("modal:title.deleteWorkflow"),
        displayComment: false,
      });
  };

  return (
    <Box
      sx={{
        mx: 1,
        backgroundColor: theme.palette.background.default,
        borderRadius: "12px",
        ...theme.helpers(theme).panelHighlights,
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        position: "relative",
      }}
    >
      <Stack direction={"row"} spacing={1}>
        <Tooltip title={t("tooltip:title.createNewWorkflow")}>
          <IconButton onClick={handleCreateNewWorkflowClick}>
            <NoteAddOutlinedIcon color="primary" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.loadWorkflow")}>
          <IconButton onClick={handleLoadWorkflowClick}>
            <FileOpenOutlinedIcon color="primary" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.saveWorkflowAs")}>
          <IconButton disabled={diagramNodes.length === 0} onClick={handleSaveAsWorkflowClick}>
            <SaveAsOutlinedIcon color="success" />
          </IconButton>
        </Tooltip>
        {!isNewWorkflow && (
          <Tooltip title={t("tooltip:title.saveWorkflow")}>
            <IconButton disabled={diagramNodes.length === 0} onClick={handleSaveWorkflowClick}>
              <SaveOutlined color="success" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Stack direction={"row"} justifyContent={"flex-end"} display={"flex"}>
        <Typography
          p={1}
          fontSize={"18px"}
          variant="h5"
          lineHeight={"24px"}
          color={workflow.name && workflow.name !== "" ? "text.primary" : "text.tier"}
        >
          {workflow.name && workflow.name !== "" ? workflow.name : "untitled"}
        </Typography>
        <Tooltip title={t("tooltip:title.changeOrientation")}>
          <IconButton onClick={handleChangeOrientationClick}>
            {orientation === "horizontal" ? (
              <HorizontalSplitOutlinedIcon color="primary" />
            ) : (
              <VerticalSplitOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title={t("tooltip:title.autoLayout")}>
          <IconButton onClick={handleAutoLayoutClick}>
            <AutoModeOutlinedIcon color="primary" />
          </IconButton>
        </Tooltip>
        {!isNewWorkflow && (
          <Tooltip title={t("tooltip:title.deleteWorkflow")}>
            <IconButton onClick={handleDeleteClick}>
              <DeleteOutlineOutlined color="error" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

export default KalikoHeader;
